/**
 * useUserRole Hook
 *
 * Custom hook for fetching user role from Firestore with real-time updates.
 * Used for admin access control in the UI.
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Real-time listener via onSnapshot
 * - NO localStorage (role comes from Firestore)
 * - Two-layer security: Client check (UX) + Server check (actual security)
 */

import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebaseConfig';
import { useAuth } from '@/contexts/AuthContext';
import { UserDocument, UserRole } from '@/shared/types';

interface UseUserRoleReturn {
  role: UserRole | null;
  loading: boolean;
  error: string | null;
  isSuperAdmin: boolean;
}

/**
 * Custom hook to fetch user role with real-time updates
 *
 * Listens to user document in Firestore for role changes.
 * Returns role, loading state, and convenience flag for super_admin check.
 *
 * @returns Role, loading state, error, and isSuperAdmin flag
 *
 * @example
 * const { role, loading, isSuperAdmin } = useUserRole();
 * if (isSuperAdmin) {
 *   // Show admin UI
 * }
 */
export function useUserRole(): UseUserRoleReturn {
  const { currentUser } = useAuth();
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser) {
      // Reset state when logged out
      setRole(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Set up real-time listener on user document
    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as UserDocument;
          console.log('useUserRole: Fetched user data', data);
          setRole(data.role ?? UserRole.USER);
          setError(null);
        } else {
          console.log('useUserRole: Document does not exist');
          setError('User document not found');
          setRole(UserRole.USER); // Default to USER if document doesn't exist
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error listening to user role:', err);
        setError('Failed to load user role');
        setRole(UserRole.USER); // Default to USER on error
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return unsubscribe;
  }, [currentUser]);

  return {
    role,
    loading,
    error,
    isSuperAdmin: role === UserRole.SUPER_ADMIN,
  };
}
